import { createTheme, alpha } from "@mui/material/styles";

const C = {
  brand: "#0d9488",
  brandDark: "#0f766e",
  brandSoft: "#ccfbf1",
  brandSoftHover: "#99f6e4",
  pageBg: "#f4f7f6",
  paper: "#ffffff",
  surface: "#fafbfb",
  text: "#0f1f1d",
  textSub: "#2e3a37",
  textMuted: "#5d6b67",
  border: "#e2eae8",
  borderStrong: "#cfd9d6",
  positive: "#10b981",
  warning: "#f59e0b",
  negative: "#ef4444",
  info: "#0ea5e9",
  rowHover: "#f0f5f3",
  rowSelected: "#ccfbf1",
};

const SHADOWS = {
  sm: "0 1px 2px 0 rgba(15,30,28,0.04)",
  md: "0 1px 2px 0 rgba(15,30,28,0.04), 0 2px 4px -1px rgba(15,30,28,0.04)",
  lg: "0 4px 12px -2px rgba(15,30,28,0.08), 0 2px 4px -2px rgba(15,30,28,0.05)",
  xl: "0 12px 24px -8px rgba(15,30,28,0.10), 0 4px 8px -4px rgba(15,30,28,0.06)",
  focus: `0 0 0 3px ${alpha("#0d9488", 0.20)}`,
};

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: C.brand,
      dark: C.brandDark,
      light: C.brandSoft,
      contrastText: "#fff",
    },
    secondary: { main: "#0891b2", light: "#cffafe" },
    background: { default: C.pageBg, paper: C.paper },
    text: { primary: C.text, secondary: C.textSub, disabled: C.textMuted },
    success: { main: C.positive, light: "#d1fae5", dark: "#047857" },
    warning: { main: C.warning, light: "#fef3c7", dark: "#b45309" },
    error: { main: C.negative, light: "#fee2e2", dark: "#b91c1c" },
    info: { main: C.info, light: "#e0f2fe" },
    divider: C.border,
    action: {
      hover: C.rowHover,
      selected: C.rowSelected,
      disabledOpacity: 0.4,
    },
  },
  shape: { borderRadius: 8 },
  spacing: 4,
  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: 13,
    htmlFontSize: 14,
    h1: { fontSize: "1.5rem", fontWeight: 600, letterSpacing: "-0.01em" },
    h2: { fontSize: "1.25rem", fontWeight: 600, letterSpacing: "-0.005em" },
    h3: { fontSize: "1.125rem", fontWeight: 600 },
    h4: { fontSize: "1rem", fontWeight: 600 },
    h5: { fontSize: "0.9375rem", fontWeight: 500 },
    h6: { fontSize: "0.875rem", fontWeight: 500 },
    body1: { fontSize: "0.8125rem", lineHeight: 1.55, fontWeight: 400 },
    body2: { fontSize: "0.75rem", lineHeight: 1.55, fontWeight: 400 },
    button: { fontSize: "0.8125rem", textTransform: "none", fontWeight: 500 },
    caption: { fontSize: "0.6875rem", color: C.textSub, lineHeight: 1.5, fontWeight: 400 },
    overline: {
      fontSize: "0.625rem",
      fontWeight: 600,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: C.textSub,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Bump global root font size by +1 px (browser default 16 → 17),
        // which scales every rem-based size proportionally upward.
        html: {
          fontSize: "17px",
        },
        // Auto-caps every form entry across the app.
        // Skips password / email / number inputs since those have their own
        // case rules. Also opts out anything with data-no-uppercase="true".
        'input:not([type="password"]):not([type="email"]):not([type="number"]):not([data-no-uppercase])':
          {
            textTransform: "uppercase",
          },
        "textarea:not([data-no-uppercase])": {
          textTransform: "uppercase",
        },
        // Placeholders should keep their authored sentence-case so labels like
        // "Search by name, phone…" don't read as "SEARCH BY NAME, PHONE…".
        "input::placeholder, textarea::placeholder": {
          textTransform: "none",
        },
        body: {
          backgroundColor: C.pageBg,
          color: C.text,
          fontFeatureSettings: '"cv11", "ss01"',
        },
        "*::-webkit-scrollbar": { width: 10, height: 10 },
        "*::-webkit-scrollbar-thumb": {
          backgroundColor: "#cdd3df",
          borderRadius: 8,
          border: "2px solid transparent",
          backgroundClip: "padding-box",
        },
        "*::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "#aab2c0",
          backgroundClip: "padding-box",
        },
        "*::-webkit-scrollbar-track": { backgroundColor: "transparent" },
        // ---- Global dropdown distinct border for plain <Select> ----
        // Autocomplete is styled via MuiAutocomplete component override.
        // For TextField with `select` prop (or raw <Select>), target the
        // OutlinedInput root that contains a .MuiSelect-select element.
        ".MuiOutlinedInput-root:has(> .MuiSelect-select)": {
          borderRadius: 8,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: alpha(C.brand, 0.45),
            borderWidth: "1.5px",
          },
          "&:hover": {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: C.brand,
            },
          },
          "&.Mui-focused": {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: C.brand,
              borderWidth: "1.5px",
            },
          },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true, size: "small" },
      styleOverrides: {
        root: {
          borderRadius: 6,
          minHeight: 30,
          paddingTop: 5,
          paddingBottom: 5,
          paddingLeft: 12,
          paddingRight: 12,
          transition:
            "background-color .15s ease, box-shadow .15s ease, transform .05s ease",
          "&:active": { transform: "translateY(0.5px)" },
        },
        contained: {
          boxShadow: SHADOWS.sm,
          "&:hover": { boxShadow: SHADOWS.md },
        },
        outlined: {
          borderColor: C.borderStrong,
          backgroundColor: "#fff",
          "&:hover": {
            borderColor: C.brand,
            backgroundColor: C.brandSoft,
          },
        },
        text: {
          "&:hover": { backgroundColor: C.brandSoft },
        },
      },
    },
    MuiIconButton: {
      defaultProps: { size: "small" },
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: 6,
          transition: "background-color .15s ease, color .15s ease",
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: "small", variant: "outlined" },
    },
    // ---- Global dropdown styling ----
    // Every Autocomplete / Select across the app gets a distinct, easy-to-
    // identify look: light teal fill, teal-tinted border, teal chevron, teal
    // bold label. Plain text TextFields are unaffected.
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: alpha(C.brand, 0.45),
              borderWidth: "1.5px",
            },
            "&:hover": {
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: C.brand,
              },
            },
            "&.Mui-focused": {
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: C.brand,
                borderWidth: "1.5px",
              },
            },
          },
          "& .MuiInputBase-input": { fontWeight: 500 },
          "& .MuiAutocomplete-popupIndicator": { color: C.brand },
          "& .MuiAutocomplete-clearIndicator": { color: C.brand },
        },
        // Bold + teal label so the dropdown reads as a picker
        // (paired with InputLabel override below which scopes by .Mui-dropdown)
        listbox: {
          padding: 0,
          "& .MuiAutocomplete-option": {
            fontSize: "0.8125rem",
            paddingTop: 6,
            paddingBottom: 6,
            borderBottom: `1px solid ${C.border}`,
            "&:last-child": { borderBottom: 0 },
            color: C.text,
            "&:hover": { backgroundColor: C.brandSoft, color: C.text },
            "&.Mui-focused": {
              backgroundColor: `${C.brandSoft} !important`,
              color: C.text,
            },
            // Selected option keeps dark text on a soft teal background so
            // it stays readable (white text on solid teal was unreadable).
            "&[aria-selected='true']": {
              backgroundColor: `${C.brandSoft} !important`,
              color: `${C.text} !important`,
              fontWeight: 600,
            },
            "&[aria-selected='true'].Mui-focused": {
              backgroundColor: `${C.brandSoftHover} !important`,
              color: `${C.text} !important`,
            },
          },
        },
        paper: {
          borderRadius: 8,
          border: `1px solid ${C.border}`,
          marginTop: 4,
          boxShadow: SHADOWS.lg,
        },
      },
    },
    // For <TextField select> / <Select> — same teal tint via the underlying
    // OutlinedInput. We can't target the OutlinedInput conditionally, so we
    // use the `.MuiSelect-select`'s parent via :has() (modern browsers).
    MuiSelect: {
      styleOverrides: {
        select: { fontWeight: 500 },
        icon: { color: C.brand },
      },
    },
    // Hint via a CSS rule on input that has a Select sibling so the wrapping
    // OutlinedInput picks up the teal fill. Lives in MuiCssBaseline below.
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          backgroundColor: "#fff",
          fontSize: "0.8125rem",
          transition: "box-shadow .15s ease",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: C.border,
            transition: "border-color .15s ease",
          },
          "& .MuiOutlinedInput-notchedOutline legend": {
            fontSize: "0.66em",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: C.borderStrong,
          },
          "&.Mui-focused": {
            boxShadow: SHADOWS.focus,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: C.brand,
              borderWidth: 1,
            },
          },
        },
        input: { padding: "7px 10px" },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { fontSize: "0.8125rem" },
        outlined: {
          transform: "translate(10px, 8px) scale(1)",
          "&.MuiInputLabel-sizeSmall": {
            transform: "translate(10px, 8px) scale(1)",
          },
          "&.MuiInputLabel-shrink": {
            transform: "translate(10px, -7px) scale(0.78)",
            backgroundColor: "#fff",
            paddingLeft: 4,
            paddingRight: 4,
            "&.MuiInputLabel-sizeSmall": {
              transform: "translate(10px, -7px) scale(0.78)",
            },
          },
        },
      },
    },
    MuiTable: { defaultProps: { size: "small" } },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "8px 12px",
          fontSize: "0.75rem",
          borderBottom: `1px solid ${C.border}`,
        },
        head: {
          backgroundColor: C.brandDark,
          color: "#ffffff",
          fontWeight: 700,
          textTransform: "uppercase",
          fontSize: "0.625rem",
          letterSpacing: "0.06em",
          padding: "10px 12px",
          borderBottom: `1px solid ${C.brandDark}`,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: "background-color .12s ease",
          "&:hover": { backgroundColor: C.rowHover },
          "&.Mui-selected": {
            backgroundColor: C.rowSelected,
            "&:hover": { backgroundColor: C.rowSelected },
          },
          "&:last-of-type td": { borderBottom: 0 },
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: `1px solid ${C.border}`,
          borderRadius: 10,
          backgroundColor: C.paper,
          transition: "border-color .15s ease, box-shadow .15s ease",
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: { padding: 16, "&:last-child": { paddingBottom: 16 } },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: "none", borderRadius: 10 },
        outlined: { border: `1px solid ${C.border}` },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: "inherit" },
      styleOverrides: {
        root: {
          backgroundColor: "rgba(255,255,255,0.85)",
          backdropFilter: "saturate(180%) blur(8px)",
          color: C.text,
          borderBottom: `1px solid ${C.border}`,
          boxShadow: "none",
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: { minHeight: "52px !important", paddingLeft: 16, paddingRight: 16 },
        dense: { minHeight: "52px !important" },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: "#fbfcfe",
          borderRight: `1px solid ${C.border}`,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          minHeight: 36,
          paddingTop: 6,
          paddingBottom: 6,
          paddingLeft: 12,
          paddingRight: 12,
          marginLeft: 8,
          marginRight: 8,
          borderRadius: 8,
          color: C.textSub,
          transition: "background-color .15s ease, color .15s ease",
          "&:hover": { backgroundColor: C.rowHover, color: C.text },
          "&.Mui-selected": {
            backgroundColor: C.brandSoft,
            color: C.brand,
            "& .MuiListItemIcon-root": { color: C.brand },
            "&:hover": { backgroundColor: C.brandSoftHover },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: { minWidth: 30, color: C.textSub },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: { fontSize: "0.8125rem", fontWeight: 500 },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: C.border } },
    },
    MuiChip: {
      defaultProps: { size: "small" },
      styleOverrides: {
        root: {
          borderRadius: 6,
          height: 22,
          fontSize: "0.6875rem",
          fontWeight: 500,
        },
        outlined: { borderColor: C.borderStrong },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: { width: 30, height: 30, fontSize: "0.75rem", fontWeight: 600 },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#1a1f36",
          color: "#fff",
          fontSize: "0.6875rem",
          borderRadius: 6,
          padding: "5px 8px",
          boxShadow: SHADOWS.lg,
        },
        arrow: { color: "#1a1f36" },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 38 },
        indicator: { height: 3, borderRadius: "3px 3px 0 0" },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 38,
          fontSize: "0.8125rem",
          fontWeight: 600,
          color: C.textSub,
          textTransform: "none",
          "&.Mui-selected": { color: C.text },
          "&:hover": { color: C.text, backgroundColor: C.rowHover },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: SHADOWS.xl,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: `1px solid ${C.border}`,
          boxShadow: SHADOWS.lg,
          borderRadius: 8,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "0.8125rem",
          minHeight: 32,
          paddingTop: 6,
          paddingBottom: 6,
          borderRadius: 6,
          margin: "2px 6px",
          color: C.text,
          "&:hover": { backgroundColor: C.rowHover, color: C.text },
          // Selected menu item keeps dark text on a soft teal background.
          "&.Mui-selected": {
            backgroundColor: `${C.brandSoft} !important`,
            color: `${C.text} !important`,
            fontWeight: 600,
            "&:hover": {
              backgroundColor: `${C.brandSoftHover} !important`,
              color: `${C.text} !important`,
            },
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: { padding: 6 },
        thumb: { boxShadow: SHADOWS.sm },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        separator: { marginLeft: 6, marginRight: 6 },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 8 },
      },
    },
    MuiPagination: {
      styleOverrides: {
        ul: { gap: 2 },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 600,
          fontSize: "0.75rem",
          minWidth: 26,
          height: 26,
        },
      },
    },
  },
});

theme.customShadows = SHADOWS;
theme.brandColors = C;

export default theme;
